import React, { FC, useCallback, useState } from 'react';

import { Button, Label } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { aliceAgentAppShareApi } from 'client/api';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceAgentAppAccess';

import { Field } from '../form/Field/Field';
import { TextInputWithCopy } from '../form/TextInputWithCopy/TextInputWithCopy';

import styles from './AliceAgentAppSharingPage.module.scss';

const t = i18n(keyset);

interface Props {
    app: AliceAgentAppDTO;
}

export const AliceAgentAppGenerateLinkSection: FC<Props> = ({ app }) => {
    const [currentKey, setCurrentKey] = useState<string | null>(app.publicShareKey ?? null);
    const [showRotationWarning, setShowRotationWarning] = useState(false);

    // URL публичной ссылки — тот же shareBaseUrl, что у обычных навыков, но другой path
    const shareLink = currentKey ? `${__appConfig.app.shareBaseUrl}/${currentKey}` : '';

    const [generateHandler, generateState] = useFetchingHandler({
        handler: () => aliceAgentAppShareApi.createOrUpdatePublicShare(app.skillId),
        throwErrors: true,
    });

    const handleGenerate = useCallback(async () => {
        try {
            const newKey = await generateHandler();
            if (newKey) {
                if (currentKey) setShowRotationWarning(true); // была старая ссылка — предупредить
                setCurrentKey(newKey);
            }
        } catch (e) {
            console.error(e);
        }
    }, [generateHandler, currentKey]);

    const isGenerating = generateState === 'loading';
    const isError = generateState === 'error';

    return (
        <div className={styles.section}>
            <Field
                contentClassName={styles.field}
                fieldTitle={t('public-link-header')}
                fieldDescription={t('public-link-description')}
                error={isError ? t('error') : undefined}
            >
                <TextInputWithCopy value={shareLink} disabled={isGenerating} />

                <Button view="action" size="l" onClick={handleGenerate} loading={isGenerating}>
                    {currentKey ? t('public-link-update-button') : t('public-link-generate-button')}
                </Button>

                {showRotationWarning && (
                    <Label theme="warning" size="s" className={styles.rotationWarning}>
                        {t('public-link-rotation-warning')}
                    </Label>
                )}
            </Field>
        </div>
    );
};
